import { ReactNode, useEffect, useCallback } from 'react'
import { twMerge } from 'tailwind-merge'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/pro-light-svg-icons'

interface DrawerProps {
  children: ReactNode
  onClose: () => void
  width?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
}

const widthMap = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl'
}

export const Drawer = ({ children, onClose, width = 'lg', className }: DrawerProps) => {
  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleEscape)
    document.body.style.overflow = 'hidden'

    const zohoWidget = document.querySelector<HTMLElement>('#zsiq_float')
    if (zohoWidget) {
      zohoWidget.style.display = 'none'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''

      if (zohoWidget) {
        zohoWidget.style.display = ''
      }
    }
  }, [handleEscape])

  return (
    <div className='fixed inset-0 z-50 flex justify-end'>
      <div className='absolute inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity' onClick={onClose} />
      <div
        className={twMerge(
          'relative w-full h-full bg-white shadow-2xl flex flex-col',
          'animate-in slide-in-from-right duration-300',
          widthMap[width],
          className
        )}
      >
        {children}
      </div>
    </div>
  )
}

interface DrawerHeaderProps {
  children: ReactNode
  onClose?: () => void
  className?: string
}

export const DrawerHeader = ({ children, onClose, className }: DrawerHeaderProps) => {
  return (
    <div
      className={twMerge('flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0', className)}
    >
      <h2 className='text-lg font-semibold text-slate-900'>{children}</h2>
      {onClose && (
        <button
          onClick={onClose}
          aria-label='Close drawer'
          className='flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors'
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      )}
    </div>
  )
}

interface DrawerBodyProps {
  children: ReactNode
  className?: string
}

export const DrawerBody = ({ children, className }: DrawerBodyProps) => {
  return <div className={twMerge('flex-1 overflow-y-auto px-6 py-4', className)}>{children}</div>
}

interface DrawerFooterProps {
  children: ReactNode
  className?: string
}

export const DrawerFooter = ({ children, className }: DrawerFooterProps) => {
  return (
    <div
      className={twMerge(
        'flex items-center justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-200 shrink-0',
        className
      )}
    >
      {children}
    </div>
  )
}

export default Drawer
